import { CreateControllerFn, ControllerParams } from '@wix/yoshi-flow-editor';
import { restoreAction, createEventPublisher } from './worker/lib';
import {
  getApiBaseUrl,
  getExperimentsFromPetri,
  getMeshOverrides,
  patchControllerConfig,
} from './worker/helpers';
import { getHydratedSource } from './worker/helpers/get-hydrated-source';
import { getCurrentSiteUser } from './worker/helpers/current-site-user';
import EVENTS from './constants/events';
import { initPublicServices } from './api/public';
import { initPublicServicesV3 } from './api/v3/public';
import { createWorkerHandlerForOOI } from './worker/createHandlerForOOI';
import { createStore, InitialData } from './worker/helpers/create-store';
import { subscribeToEvents } from './worker/helpers/subscribe-to-events';
import { getStoreBuilderForOOI } from './worker/helpers/get-store-builder-for-ooi';
import { getSeoData } from './worker/helpers/getSeoData';
import { isWixCodeSEOEnabled } from '@wix/wix-vod-shared/dist/src/common/utils/experiments';
import { createHandlers } from './worker/controller-handlers';
import { initPrivateServices } from './api/private';

export const createController =
  (
    createWorkerHandler: Function,
    getStoreBuilder: Function,
    isModal: boolean,
  ): CreateControllerFn =>
  async (context: ControllerParams) => {
    const {
      controllerConfig: originalControllerConfig,
      flowAPI: {
        environment: { isSSR, isMobile, isViewer },
        translations,
        reportError,
        httpClient,
      },
      flowAPI,
    } = context;
    const {
      wixCodeApi,
      platformAPIs,
      setProps,
      appParams: { instance },
    } = originalControllerConfig;
    const baseUrl = getApiBaseUrl(wixCodeApi);
    const { publishEvent, getEvents, consumeEvents } = createEventPublisher({
      setProps: originalControllerConfig.setProps,
    });
    const controllerConfig = patchControllerConfig(originalControllerConfig, {
      getEvents,
    });
    const experiments = await getExperimentsFromPetri(
      controllerConfig,
      isViewer,
    );
    const debug = process.env.NODE_ENV === 'development';

    let pendingCurrentSiteUser: any;

    const handleLogin = async (event: any) => {
      const currentSiteUser = await getCurrentSiteUser(event);

      platformAPIs.pubSub.publish(
        EVENTS.SESSION.LOGIN_USER,
        currentSiteUser,
        false,
      );

      pendingCurrentSiteUser = currentSiteUser;
    };

    wixCodeApi.user.onLogin(handleLogin);

    initPrivateServices(httpClient);
    initPublicServices(httpClient, instance, baseUrl);
    initPublicServicesV3(httpClient, instance, baseUrl);

    const hydratedSource = await getHydratedSource(controllerConfig, flowAPI, {
      experiments,
      isModal,

      // a ? sign is for modal, in modal it's undefined. Not sure why, but since it's not used for modal anyway,
      // I'll leave it like this for now
      translations: translations?.all,
    });

    return {
      async pageReady() {
        const currentPageId = wixCodeApi.site.currentPage?.id;

        const pageReadyData = {
          currentPageId,
        };

        const initialData: InitialData = {
          ...hydratedSource,
          ...pageReadyData,
        };
        const { widgetData } = hydratedSource;

        if (isWixCodeSEOEnabled() && widgetData.isV3Api) {
          const getSeoDataParams = widgetData.isSingleVideo
            ? {
                videos: [widgetData.__SINGLE_VIDEO__],
              }
            : {
                channel: widgetData.__CHANNEL__,
                videos: widgetData.__VIDEOS__.items,
              };
          const seoData = getSeoData(getSeoDataParams);
          wixCodeApi.seo.renderSEOTags({
            itemType: 'VIDEO',
            itemData: seoData,
          });
        }

        const workerHandler = createWorkerHandler(
          controllerConfig,
          {
            debug,
          },
          flowAPI,
        );

        const dispatchEv = (action: any) =>
          workerHandler(restoreAction({ publishEvent, action }));

        if (pendingCurrentSiteUser) {
          initialData.__CURRENT_SITE_USER__ = pendingCurrentSiteUser;
        }

        const { handlers, getControllerState } = createHandlers(context);
        const storeProps = await createStore(
          getStoreBuilder,
          controllerConfig,
          initialData,
          setProps,
          debug,
          isSSR,
          isMobile,
          handlers,
          getControllerState,
        );

        const componentProps = {
          ...storeProps,
          dispatchEv,
          consumeEvents,
          captureException: reportError,
          renderingEnv: wixCodeApi.window.rendering.env,
          experiments,
          debug,
          isSSR,
          instance,
          handlers,
          ...getMeshOverrides(),
        };

        setProps(componentProps);

        // we cant publish events before initial setProps (check on invalid video id from url)
        subscribeToEvents(setProps, platformAPIs.pubSub);

        return componentProps.appState;
      },
    };
  };

export default createController(
  createWorkerHandlerForOOI,
  getStoreBuilderForOOI,
  false,
);
